'use client';

import { Fragment, useCallback, useEffect, useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Copy, Trash2, Plus, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DeckShareStats } from './deck-share-stats';

interface DeckShare {
  id: string;
  code: string;
  url: string;
  label: string | null;
  ownerId: string;
  createdAt: string;
  expiresAt: string | null;
  views: number;
  signedUp: boolean;
  uniqueSessions: number;
  deckSlug: string;
  allowAll: boolean;
}

interface DeckVariantSummary {
  slug: string;
  label: string;
  tag: string;
  isDefault: boolean;
  slideCount: number;
}

export function DeckTab() {
  const [shares, setShares] = useState<DeckShare[]>([]);
  const [variants, setVariants] = useState<DeckVariantSummary[]>([]);
  const [fetching, setFetching] = useState(true);
  const [label, setLabel] = useState('');
  const [days, setDays] = useState<string>('');
  const [variantSlug, setVariantSlug] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [filterSlug, setFilterSlug] = useState<string>('all');

  const fetchShares = useCallback(async () => {
    setFetching(true);
    try {
      const res = await fetch('/api/admin/deck/share');
      if (res.ok) {
        const data = await res.json();
        setShares(data.shares || []);
      }
    } finally {
      setFetching(false);
    }
  }, []);

  const fetchVariants = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/deck/variants');
      if (res.ok) {
        const data = await res.json();
        const list: DeckVariantSummary[] = data.variants || [];
        setVariants(list);
        // Pre-select the default variant for the mint form on first load.
        setVariantSlug((prev) => prev || data.defaultSlug || list[0]?.slug || '');
      }
    } catch {
      // Variants endpoint failure shouldn't break the share list.
    }
  }, []);

  useEffect(() => {
    fetchShares();
    fetchVariants();
  }, [fetchShares, fetchVariants]);

  const handleMint = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {};
      if (label.trim()) body.label = label.trim();
      const d = parseInt(days, 10);
      if (Number.isFinite(d) && d > 0) body.days = d;
      if (variantSlug) body.deckSlug = variantSlug;
      const res = await fetch('/api/admin/deck/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || 'Failed to mint share');
        return;
      }
      setLabel('');
      setDays('');
      await fetchShares();
    } catch {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (code: string) => {
    if (!confirm(`Revoke share "${code}"? Future visits will be blocked, but historical attribution is preserved.`)) {
      return;
    }
    await fetch(`/api/admin/deck/share?code=${encodeURIComponent(code)}`, { method: 'DELETE' });
    await fetchShares();
  };

  const handleCopy = async (url: string, code: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    } catch {
      // ignore — fall back to manual copy
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold">Deck shares</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Mint trackable share codes per deck variant. Each code is a regular
          referral code under the hood, so any signup attributed to it credits you.
        </p>
      </div>

      <VariantRollup variants={variants} />


      <form onSubmit={handleMint} className="card p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_auto] gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Sequoia / Doug"
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Variant</label>
            <select
              value={variantSlug}
              onChange={(e) => setVariantSlug(e.target.value)}
              disabled={variants.length === 0}
              className="mt-1 w-full px-2 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {variants.length === 0 && <option value="">…</option>}
              {variants.map((v) => (
                <option key={v.slug} value={v.slug}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Expires (days)</label>
            <input
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="∞"
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1.5 disabled:opacity-60"
            >
              {submitting ? <Spinner size="sm" /> : <Plus className="h-4 w-4" />}
              Mint
            </button>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </form>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <h3 className="text-sm font-semibold">Existing shares</h3>
          {variants.length > 1 && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Filter</span>
              <select
                value={filterSlug}
                onChange={(e) => setFilterSlug(e.target.value)}
                className="px-2 py-1 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">All variants</option>
                {variants.map((v) => (
                  <option key={v.slug} value={v.slug}>{v.label}</option>
                ))}
              </select>
            </div>
          )}
          <span className={cn('text-xs text-muted-foreground', variants.length > 1 ? '' : 'ml-auto')}>
            {shares.length}
          </span>
        </div>
        {fetching ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" tone="muted" />
          </div>
        ) : shares.length === 0 ? (
          <p className="text-sm text-muted-foreground px-4 py-6 text-center">
            No deck shares minted yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 w-8" />
                  <th className="text-left px-4 py-2">Label</th>
                  <th className="text-left px-3 py-2">Variant</th>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-right px-4 py-2">Sessions</th>
                  <th className="text-right px-4 py-2">Slide views</th>
                  <th className="text-center px-4 py-2">Signed up</th>
                  <th className="text-left px-4 py-2">Created</th>
                  <th className="text-left px-4 py-2">Expires</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {shares
                  .filter((s) => filterSlug === 'all' || s.deckSlug === filterSlug)
                  .map((s) => {
                  const expired = s.expiresAt && new Date(s.expiresAt).getTime() < Date.now();
                  const isOpen = expandedCode === s.code;
                  const ChevronIcon = isOpen ? ChevronDown : ChevronRight;
                  return (
                    <Fragment key={s.id}>
                      <tr className={cn('group', expired && 'opacity-50')}>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            onClick={() => setExpandedCode(isOpen ? null : s.code)}
                            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                            title={isOpen ? 'Hide stats' : 'Show stats'}
                            aria-expanded={isOpen}
                          >
                            <ChevronIcon className="h-4 w-4" />
                          </button>
                        </td>
                        <td className="px-4 py-2 font-medium">
                          {s.label || <span className="text-muted-foreground italic">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary border border-primary/20"
                            title={s.allowAll ? 'Unlocks any variant' : `Bound to ${s.deckSlug}`}
                          >
                            {variants.find((v) => v.slug === s.deckSlug)?.tag ?? s.deckSlug}
                            {s.allowAll && <span className="opacity-70">·any</span>}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{s.code}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{s.uniqueSessions}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{s.views}</td>
                        <td className="px-4 py-2 text-center">
                          {s.signedUp ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                              yes
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {new Date(s.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {s.expiresAt ? new Date(s.expiresAt).toLocaleDateString() : 'never'}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              type="button"
                              onClick={() => handleCopy(s.url, s.code)}
                              className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                              title="Copy URL"
                            >
                              {copiedCode === s.code ? (
                                <span className="text-xs text-emerald-500">✓</span>
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                              title="Open"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                            <button
                              type="button"
                              onClick={() => handleRevoke(s.code)}
                              disabled={!!expired}
                              className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-30"
                              title="Revoke"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-muted/10">
                          <td colSpan={10} className="p-0">
                            <DeckShareStats code={s.code} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface VariantStats {
  slug: string;
  label: string;
  tag: string;
  sessions: number;
  opens: number;
  completes: number;
  completionRate: number;
  uniqueRecipients: number;
  signedUp: number;
  linkClicks: number;
  composerSubmits: number;
  dwellMs: number;
  totalShares: number;
}

function fmtMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function VariantRollup({ variants: knownVariants }: { variants: DeckVariantSummary[] }) {
  const [stats, setStats] = useState<VariantStats[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/admin/deck/variants/stats')
      .then((r) => (r.ok ? r.json() : { variants: [] }))
      .then((d) => {
        if (!cancelled) {
          setStats(d.variants ?? []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (knownVariants.length <= 1) return null;
  if (loading) {
    return (
      <div className="card p-4 flex items-center justify-center">
        <Spinner size="sm" tone="muted" />
      </div>
    );
  }
  if (!stats || stats.length === 0) return null;

  const maxSessions = Math.max(1, ...stats.map((s) => s.sessions));

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <h3 className="text-sm font-semibold">Variants</h3>
        <span className="text-xs text-muted-foreground ml-auto">
          per-variant rollup across all shares
        </span>
      </div>
      <div className="divide-y divide-border/50">
        {stats.map((v) => {
          const sessionPct = (v.sessions / maxSessions) * 100;
          return (
            <div key={v.slug} className="px-4 py-3 grid grid-cols-1 lg:grid-cols-[200px_1fr_auto] items-center gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{v.label}</span>
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    {v.slug}
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                  {v.totalShares} share{v.totalShares === 1 ? '' : 's'}
                </div>
              </div>
              <div>
                <div className="relative h-5 rounded bg-muted/40 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary/60 flex items-center px-2 text-[10px] tabular-nums text-primary-foreground"
                    style={{ width: `${Math.max(2, sessionPct)}%` }}
                  >
                    {sessionPct >= 18 ? `${v.sessions} sessions` : ''}
                  </div>
                  {sessionPct < 18 && (
                    <span className="absolute inset-y-0 left-2 flex items-center text-[10px] tabular-nums text-foreground/80">
                      {v.sessions} sessions
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4 text-[11px] tabular-nums whitespace-nowrap">
                <RollupStat label="Opens" value={v.opens} />
                <RollupStat
                  label="Completion"
                  value={v.opens > 0 ? `${Math.round(v.completionRate * 100)}%` : '—'}
                  sub={`${v.completes}/${v.opens}`}
                />
                <RollupStat label="Recipients" value={v.uniqueRecipients} sub={`${v.signedUp} signed up`} />
                <RollupStat label="Total time" value={fmtMs(v.dwellMs)} sub={`${v.linkClicks} clicks`} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RollupStat({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | string;
  sub?: string;
}) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-[9px] text-muted-foreground tabular-nums">{sub}</div>}
    </div>
  );
}

